export type OpsOrbAnalyserProducer = Readonly<{
  id: symbol;
  label: string;
}>;

export type OpsOrbAnalyserReceipt = Readonly<{
  producer: OpsOrbAnalyserProducer;
  generation: number;
}>;

export type OpsOrbAnalyserSnapshot = Readonly<{
  generation: number;
  producerLabel: string | null;
  analyser: AnalyserNode | null;
}>;

type OpsOrbAnalyserListener = (snapshot: OpsOrbAnalyserSnapshot) => void;

/**
 * Adapted from the replayable output-analyser owner proven in Seen Stage/KAI
 * commit c139b77d. This copy is page-local Ops source: it imports no donor
 * runtime, state, service, account, or credential.
 */
export function createOpsOrbAnalyserStore() {
  let generation = 0;
  const claims = new Map<symbol, Readonly<{
    receipt: OpsOrbAnalyserReceipt;
    analyser: AnalyserNode;
  }>>();
  let snapshot: OpsOrbAnalyserSnapshot = Object.freeze({
    generation,
    producerLabel: null,
    analyser: null
  });
  const listeners = new Set<OpsOrbAnalyserListener>();

  const newestClaim = () => {
    let newest: Readonly<{
      receipt: OpsOrbAnalyserReceipt;
      analyser: AnalyserNode;
    }> | null = null;
    for (const claim of claims.values()) {
      if (!newest || claim.receipt.generation > newest.receipt.generation) newest = claim;
    }
    return newest;
  };

  const notify = () => {
    for (const listener of listeners) listener(snapshot);
  };

  const publishSnapshot = (claim: ReturnType<typeof newestClaim>) => {
    generation += 1;
    snapshot = Object.freeze({
      generation,
      producerLabel: claim?.receipt.producer.label ?? null,
      analyser: claim?.analyser ?? null
    });
    notify();
  };

  return {
    createProducer(label: string): OpsOrbAnalyserProducer {
      return Object.freeze({ id: Symbol(label), label });
    },

    publish(producer: OpsOrbAnalyserProducer, analyser: AnalyserNode): OpsOrbAnalyserReceipt {
      generation += 1;
      const receipt = Object.freeze({ producer, generation });
      claims.set(producer.id, Object.freeze({ receipt, analyser }));
      snapshot = Object.freeze({ generation, producerLabel: producer.label, analyser });
      notify();
      return receipt;
    },

    clear(receipt: OpsOrbAnalyserReceipt | null): boolean {
      // WHY [BOL-ORB-ANALYSER-LEASE-001]: an older audio producer may finish
      // after a newer answer has started. Only its exact generation receipt may
      // clear its claim; if another still-audible source remains, restore that
      // source instead of dropping the Orb to synthetic motion. Falsifier: a
      // stale cleanup freezes/zeros the current answer or wins over newer PCM.
      if (!receipt) return false;
      const claim = claims.get(receipt.producer.id);
      if (claim?.receipt.generation !== receipt.generation) return false;
      const wasVisible = newestClaim()?.receipt.generation === receipt.generation;
      claims.delete(receipt.producer.id);
      if (wasVisible) publishSnapshot(newestClaim());
      return wasVisible;
    },

    getSnapshot(): OpsOrbAnalyserSnapshot {
      return snapshot;
    },

    subscribe(listener: OpsOrbAnalyserListener): () => void {
      listeners.add(listener);
      // WHY [BOL-ORB-ANALYSER-REPLAY-001]: React may mount or remount after
      // output begins. Immediate replay makes the current producer observable;
      // a one-shot event would leave an audibly speaking Luna with a dead Orb.
      // Falsifier: publish-before-mount or remount loses the current analyser.
      listener(snapshot);
      return () => listeners.delete(listener);
    }
  };
}

export const opsOrbAnalyserStore = createOpsOrbAnalyserStore();
